import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { z } from "zod";
import bcrypt from "bcrypt";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";

export const listUsers = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("users", "manage_users");
  const db = getDb();
  return db
    .select({
      id: schema.users.id,
      email: schema.users.email,
      name: schema.users.name,
      isActive: schema.users.isActive,
      roleName: schema.roles.name,
      roleId: schema.users.roleId,
      createdAt: schema.users.createdAt,
    })
    .from(schema.users)
    .innerJoin(schema.roles, eq(schema.users.roleId, schema.roles.id));
});

export const listRoles = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("users", "manage_users");
  return getDb().select().from(schema.roles);
});

export const createUser = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      email: z.string().email(),
      name: z.string().min(1),
      password: z.string().min(8),
      roleId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }) => {
    await requirePermission("users", "manage_users");
    const passwordHash = await bcrypt.hash(data.password, 12);
    const [row] = await getDb()
      .insert(schema.users)
      .values({
        email: data.email.toLowerCase(),
        name: data.name,
        passwordHash,
        roleId: data.roleId,
        isActive: true,
      })
      .returning({ id: schema.users.id, email: schema.users.email, name: schema.users.name });
    return row;
  });

export const updateUser = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      id: z.string().uuid(),
      name: z.string().optional(),
      roleId: z.string().uuid().optional(),
      isActive: z.boolean().optional(),
      password: z.string().min(8).optional(),
    }),
  )
  .handler(async ({ data }) => {
    await requirePermission("users", "manage_users");
    const updates: Record<string, unknown> = { updatedAt: new Date() };
    if (data.name) updates.name = data.name;
    if (data.roleId) updates.roleId = data.roleId;
    if (data.isActive !== undefined) updates.isActive = data.isActive;
    if (data.password) updates.passwordHash = await bcrypt.hash(data.password, 12);
    await getDb().update(schema.users).set(updates).where(eq(schema.users.id, data.id));
    return { ok: true };
  });
